/**
 * Buchhaltung & Rechnungslegung - Hauptseite
 * Orchestriert Sub-Tabs als eigenständige Komponenten.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Euro, Bell, Scale, Receipt, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';
import { useTabs } from '@/contexts/TabContext';
import { useNavigate } from 'react-router-dom';
import { TAB_EVENTS } from '@/lib/tabEvents';
import { useTabSearchParams } from '@/hooks/useTabRoute';

import {
  UebersichtTab,
  KontenTab,
  UmsaetzeTab,
  BelegeTab,
  VerbindlichkeitenTab,
  KategorienTab,
  RechnungslegungTab
} from './buchhaltung';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function BuchhaltungPage() {
  const [searchParams] = useTabSearchParams();
  const urlKlientId = searchParams.get('klient_id');
  const urlTab = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState(urlTab || 'uebersicht');
  const [klienten, setKlienten] = useState([]);
  const [selectedKlient, setSelectedKlient] = useState(urlKlientId || '');
  const [konten, setKonten] = useState([]);
  const [umsaetze, setUmsaetze] = useState([]);
  const [belege, setBelege] = useState([]);
  const [verbindlichkeiten, setVerbindlichkeiten] = useState([]);
  const [glaeubiger, setGlaeubiger] = useState([]);
  const [erinnerungen, setErinnerungen] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [bankverbindungen, setBankverbindungen] = useState([]);
  const [loading, setLoading] = useState(true);

  const { openTab } = useTabs();
  const navigate = useNavigate();

  // URL-Parameter übernehmen wenn sich die Tab-URL ändert
  useEffect(() => {
    if (urlKlientId && urlKlientId !== selectedKlient) {
      setSelectedKlient(urlKlientId);
    }
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKlientId, urlTab]);

  const openChildTab = useCallback((path, options) => {
    openTab('/buchhaltung', { title: 'Buchhaltung' });
    openTab(path, options);
    navigate(path);
  }, [openTab, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [kl, er, kat] = await Promise.all([
        betreuungAPI.listKlienten(),
        fetch(`${BACKEND_URL}/api/buchhaltung/erinnerungen`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/buchhaltung/kategorien/erweitert`).then(r => r.json()),
      ]);
      setKlienten(kl);
      setErinnerungen(er);
      setKategorien(kat);

      if (selectedKlient) {
        const [ko, um, bv, be, vb, gl] = await Promise.all([
          fetch(`${BACKEND_URL}/api/buchhaltung/konten?klient_id=${selectedKlient}`).then(r => r.json()),
          fetch(`${BACKEND_URL}/api/buchhaltung/umsaetze?klient_id=${selectedKlient}`).then(r => r.json()),
          fetch(`${BACKEND_URL}/api/buchhaltung/bankverbindungen`).then(r => r.json()),
          fetch(`${BACKEND_URL}/api/buchhaltung/belege?klient_id=${selectedKlient}`).then(r => r.json()),
          fetch(`${BACKEND_URL}/api/buchhaltung/verbindlichkeiten?klient_id=${selectedKlient}`).then(r => r.json()),
          fetch(`${BACKEND_URL}/api/buchhaltung/glaeubiger?klient_id=${selectedKlient}`).then(r => r.json()),
        ]);
        setKonten(ko);
        setUmsaetze(um);
        setBankverbindungen(bv.filter(b => ko.some(k => k.id === b.konto_id)));
        setBelege(be);
        setVerbindlichkeiten(vb);
        setGlaeubiger(gl);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedKlient]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const handleRefresh = (e) => {
      if (!e.detail?.scope || e.detail.scope === 'buchhaltung') loadData();
    };
    const handleCreated = (e) => {
      if (['konto', 'buchung', 'kategorie'].includes(e.detail?.type)) loadData();
    };
    window.addEventListener(TAB_EVENTS.DATA_REFRESH, handleRefresh);
    window.addEventListener(TAB_EVENTS.ENTITY_CREATED, handleCreated);
    return () => {
      window.removeEventListener(TAB_EVENTS.DATA_REFRESH, handleRefresh);
      window.removeEventListener(TAB_EVENTS.ENTITY_CREATED, handleCreated);
    };
  }, [loadData]);

  const handleKlientChange = (v) => {
    setSelectedKlient(v === 'none' ? '' : v);
  };

  const selectedKlientName = klienten.find(k => k.id === selectedKlient);

  const handleDeleteKonto = async (id) => {
    if (!window.confirm('Konto wirklich löschen?')) return;
    await fetch(`${BACKEND_URL}/api/buchhaltung/konten/${id}`, { method: 'DELETE' });
    toast.success('Konto gelöscht');
    loadData();
  };

  const handleSyncBankverbindung = async (verbindungId, disconnect) => {
    if (disconnect) {
      await fetch(`${BACKEND_URL}/api/buchhaltung/finapi/verbindung/${verbindungId}`, { method: 'DELETE' });
      toast.success('Verbindung getrennt');
      loadData();
      return;
    }
    try {
      toast.info('Synchronisiere Transaktionen...');
      const result = await fetch(`${BACKEND_URL}/api/buchhaltung/finapi/sync/${verbindungId}`, { method: 'POST' }).then(r => r.json());
      if (result.success) {
        toast.success(`${result.importiert} neue Transaktionen importiert`);
        loadData();
      } else {
        toast.error('Synchronisation fehlgeschlagen');
      }
    } catch {
      toast.error('Fehler bei Synchronisation');
    }
  };

  const handleKategorisieren = async (umsatzId, kategorie) => {
    await fetch(`${BACKEND_URL}/api/buchhaltung/umsaetze/${umsatzId}/kategorisieren`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kategorie })
    });
    loadData();
  };

  // Statistiken
  const einnahmenSumme = umsaetze.filter(u => u.betrag >= 0).reduce((s, u) => s + u.betrag, 0);
  const ausgabenSumme = Math.abs(umsaetze.filter(u => u.betrag < 0).reduce((s, u) => s + u.betrag, 0));
  const summeVerbindlichkeiten = verbindlichkeiten.reduce((s, v) => s + (v.aktueller_stand || 0), 0);
  const belegeOhneZuordnung = belege.filter(b => !b.umsatz_id).length;

  return (
    <div className="space-y-6" data-testid="buchhaltung-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Euro className="w-6 h-6" /> Buchhaltung & Rechnungslegung
          </h1>
          <p className="text-slate-500 mt-1">Konten, Umsätze und Belege verwalten</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedKlient || 'none'} onValueChange={handleKlientChange}>
            <SelectTrigger className="w-56" data-testid="select-klient">
              <SelectValue placeholder="Betreuter auswählen..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Betreuter wählen --</SelectItem>
              {klienten.map(k => (
                <SelectItem key={k.id} value={k.id}>{k.vorname} {k.nachname}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Erinnerungen */}
      {erinnerungen.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-5 h-5 text-amber-600" />
            <p className="font-medium text-amber-800">Erinnerungen ({erinnerungen.length})</p>
          </div>
          <div className="space-y-1">
            {erinnerungen.slice(0, 5).map((e, i) => (
              <p key={i} className="text-sm text-amber-700">{e.meldung}</p>
            ))}
          </div>
        </div>
      )}

      {!selectedKlient ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CreditCard className="w-14 h-14 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-2">Bitte wählen Sie einen Betreuten aus</p>
            <p className="text-sm text-slate-400">um Konten und Umsätze zu verwalten</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-auto flex w-full overflow-x-auto no-scrollbar p-1 gap-1">
            <TabsTrigger value="uebersicht" className="shrink-0">Übersicht</TabsTrigger>
            <TabsTrigger value="konten" className="shrink-0">Konten</TabsTrigger>
            <TabsTrigger value="umsaetze" className="shrink-0">Umsätze</TabsTrigger>
            <TabsTrigger value="belege" className="shrink-0">
              Belege {belegeOhneZuordnung > 0 && <Badge variant="destructive" className="ml-1 text-xs">{belegeOhneZuordnung}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="verbindlichkeiten" className="shrink-0">
              Verbindlichkeiten {verbindlichkeiten.length > 0 && <Badge variant="outline" className="ml-1 text-xs">{verbindlichkeiten.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="kategorien" className="shrink-0">Kategorien</TabsTrigger>
            <TabsTrigger value="rechnungslegung" className="shrink-0">Rechnungslegung</TabsTrigger>
          </TabsList>

          <TabsContent value="uebersicht">
            <UebersichtTab
              konten={konten} umsaetze={umsaetze} belege={belege}
              verbindlichkeiten={verbindlichkeiten} glaeubiger={glaeubiger}
              bankverbindungen={bankverbindungen} erinnerungen={erinnerungen}
              kategorien={kategorien}
              einnahmenSumme={einnahmenSumme} ausgabenSumme={ausgabenSumme}
              summeVerbindlichkeiten={summeVerbindlichkeiten}
              belegeOhneZuordnung={belegeOhneZuordnung}
              openChildTab={openChildTab}
              handleRechnungslegung={() => setActiveTab('rechnungslegung')}
              selectedKlient={selectedKlient}
            />
          </TabsContent>

          <TabsContent value="konten">
            <KontenTab
              konten={konten} bankverbindungen={bankverbindungen}
              selectedKlient={selectedKlient} selectedKlientName={selectedKlientName}
              handleSyncBankverbindung={handleSyncBankverbindung}
              handleDeleteKonto={handleDeleteKonto}
              openChildTab={openChildTab}
            />
          </TabsContent>

          <TabsContent value="umsaetze">
            <UmsaetzeTab
              umsaetze={umsaetze} kategorien={kategorien}
              handleKategorisieren={handleKategorisieren}
              openChildTab={openChildTab}
              selectedKlient={selectedKlient} konten={konten}
            />
          </TabsContent>

          <TabsContent value="belege">
            <BelegeTab
              belege={belege} selectedKlient={selectedKlient}
              loadData={loadData} openChildTab={openChildTab}
            />
          </TabsContent>

          <TabsContent value="verbindlichkeiten">
            <VerbindlichkeitenTab
              verbindlichkeiten={verbindlichkeiten} glaeubiger={glaeubiger}
              selectedKlient={selectedKlient}
              loadData={loadData} openChildTab={openChildTab}
            />
          </TabsContent>

          <TabsContent value="kategorien">
            <KategorienTab kategorien={kategorien} openChildTab={openChildTab} />
          </TabsContent>

          <TabsContent value="rechnungslegung">
            <RechnungslegungTab
              selectedKlient={selectedKlient}
              selectedKlientName={selectedKlientName}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
